import React, { useEffect, useState } from 'react';
import { X, ClipboardCheck, Activity, History } from 'lucide-react';
import { API_BASE_URL } from '../utils/apiConfig';
import { supabase } from '../utils/supabase';
import MonthlyProgressSummary, { MonthlyProgressSummary as SummaryType } from '../app/member/components/MonthlyProgressSummary';
import { MetricRenderer } from '../app/member/components/MetricDisplayComponents';
import LogStaffProgressModal from './LogStaffProgressModal';

interface MemberProgressGymModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
}

interface ReportListItem {
  id: string;
  reportMonth: string;
  fitnessScore: number;
  metricCount: number;
  metrics: Array<{
    metricValue: number | null;
    metricDefinition: {
      metricKey: string;
      displayName: string;
      metricCategory: string;
      displayType: string;
      metricUnit: string | null;
      improvementDirection: 'increase' | 'decrease';
    };
  }>;
}

const MemberProgressGymModal: React.FC<MemberProgressGymModalProps> = ({ isOpen, onClose, memberId }) => {
  const [latestSummary, setLatestSummary] = useState<SummaryType | null>(null);
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [memberName, setMemberName] = useState<string>('Member');
  const [memberPhoto, setMemberPhoto] = useState<string | null>(null);
  const [streak, setStreak] = useState(0);

  const lastFetchedRef = React.useRef<string | null>(null);

  const fetchData = React.useCallback(async () => {
    if (!memberId || lastFetchedRef.current === memberId) return;
    
    lastFetchedRef.current = memberId;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE_URL}/api/progress/${memberId}`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });
      const result = await res.json();
      
      if (res.ok && result.success) {
        const data = result.data;
        setLatestSummary(data.latestSummary);
        setReports(data.reports || []);
        setStreak(data.streakMonths || 0);
        
        if (data.healthProfile?.fullName) {
          setMemberName(data.healthProfile.fullName);
        } else if (data.healthProfile?.displayName) {
          setMemberName(data.healthProfile.displayName);
        }
        
        if (data.profilePhoto) {
          setMemberPhoto(data.profilePhoto);
        }
      } else if (res.status === 403) {
        setError('Member has not shared their progress with the gym.');
      } else {
        setError(result.error || 'Failed to load progress data');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Connection error. Please try again.');
      lastFetchedRef.current = null; // Allow retry
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    } else {
      lastFetchedRef.current = null; // Reset when closed
    }
  }, [isOpen, fetchData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 animate-in fade-in duration-300">
      <div 
        className="bg-white w-full max-w-6xl h-full sm:h-[90vh] sm:rounded-[3rem] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 sm:p-8 border-b border-gray-100 gap-4 sm:gap-6 shrink-0 relative">
          <div className="flex items-center gap-4 sm:gap-5">
            {memberPhoto ? (
              <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-xl shadow-[#AAD816]/20 bg-zinc-100 border-2 border-[#AAD816]">
                <img src={memberPhoto} alt={memberName} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-14 h-14 bg-[#AAD816] rounded-2xl shadow-xl shadow-[#AAD816]/20 flex items-center justify-center">
                <Activity className="w-7 h-7 text-black" />
              </div>
            )}
            <div className="space-y-0.5 sm:space-y-1">
              <h2 className="text-xl sm:text-3xl font-black text-gray-900 italic uppercase tracking-tighter">
                {memberName}<span className="text-[#AAD816]">.AI</span>
              </h2>
              <div className="flex items-center gap-2">
                 <span className="px-2 py-0.5 bg-gray-100 text-[8px] sm:text-[9px] font-black text-gray-400 rounded uppercase tracking-widest">Coaching Portal</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <button 
              onClick={fetchData}
              className="p-3 bg-gray-50 text-gray-500 rounded-xl hover:bg-gray-100 transition-all active:scale-95"
              title="Refresh Data"
            >
              <History className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setShowLogModal(true)}
              className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 sm:py-3 bg-[#AAD816] text-black rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest hover:scale-[1.02] transition-all shadow-xl shadow-[#AAD816]/10 active:scale-95 flex items-center justify-center gap-2 sm:gap-3"
            >
              <ClipboardCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Manage
            </button>
            <button 
              onClick={onClose}
              className="sm:p-3 p-2 bg-gray-900 text-white rounded-lg sm:rounded-xl hover:bg-gray-800 transition-all active:scale-95"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-white custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full py-20">
              <div className="w-10 h-10 border-4 border-[#AAD816] border-t-transparent rounded-full animate-spin" />
              <p className="mt-4 text-gray-400 font-bold uppercase tracking-widest text-[9px]">Analyzing Biometrics...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center space-y-6">
              <div className="w-20 h-20 bg-gray-50 border-2 border-dashed border-gray-200 rounded-full flex items-center justify-center mx-auto text-4xl">
                🔒
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight">Access Restricted</h3>
                <p className="text-gray-500 text-sm max-w-xs mx-auto">{error}</p>
              </div>
            </div>
          ) : reports.length === 0 ? (
            <div className="bg-gray-50 rounded-[1.5rem] sm:rounded-[2.5rem] p-8 sm:p-16 text-center space-y-6 sm:space-y-8 border-2 border-dashed border-gray-200">
                 <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center mx-auto text-2xl sm:text-3xl shadow-sm italic font-black text-gray-300">?</div>
                 <div className="space-y-2">
                    <h3 className="text-lg sm:text-xl font-black text-gray-900 uppercase italic">No Clinical Data Found</h3>
                    <p className="text-gray-500 max-w-md mx-auto text-xs sm:text-sm font-medium">This member hasn't logged any progress metrics yet.</p>
                 </div>
                 <button 
                  onClick={() => setShowLogModal(true)}
                  className="px-8 py-4 bg-black text-[#AAD816] rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all"
                 >
                   Initialize First Assessment
                 </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-10">
              <div className="xl:col-span-2 space-y-12">
                {latestSummary && (
                  <MonthlyProgressSummary summary={latestSummary} theme="light" />
                )}

                {/* Categorized Metrics Grid */}
                {(() => {
                    const latestReport = reports[0];
                    if (!latestReport) return null;

                    const categories: Record<string, typeof latestReport.metrics> = {};
                    latestReport.metrics.forEach(m => {
                        const cat = m.metricDefinition.metricCategory;
                        if (!categories[cat]) categories[cat] = [];
                        categories[cat].push(m);
                    });

                    const catOrder = [
                        'Body Composition', 
                        'Strength', 
                        'Bodyweight Strength', 
                        'Endurance', 
                        'Cardio', 
                        'Flexibility / Mobility', 
                        'Performance Tests'
                    ];
                    
                    const availableCats = Object.keys(categories).sort((a, b) => {
                        const idxA = catOrder.indexOf(a);
                        const idxB = catOrder.indexOf(b);
                        if (idxA === -1 && idxB === -1) return a.localeCompare(b);
                        if (idxA === -1) return 1;
                        if (idxB === -1) return -1;
                        return idxA - idxB;
                    });

                    return availableCats.map(cat => (
                        <div key={cat} className="space-y-6">
                            <div className="flex items-center gap-3 px-1">
                                <div className="w-1.5 h-6 bg-[#AAD816] rounded-full" />
                                <h3 className="text-lg font-black text-gray-900 italic uppercase tracking-tight">
                                    {cat.split(' ')[0]} <span className="text-[#AAD816]">{cat.split(' ').slice(1).join(' ')}</span>
                                </h3>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {categories[cat].map(m => {
                                    const mKey = m.metricDefinition.metricKey;
                                    const history = reports
                                        .filter(r => r.metrics.some(rm => rm.metricDefinition.metricKey === mKey))
                                        .map(r => ({
                                            metric_date: r.reportMonth,
                                            metric_value: Number(r.metrics.find(rm => rm.metricDefinition.metricKey === mKey)?.metricValue || 0)
                                        }))
                                        .sort((a, b) => new Date(a.metric_date).getTime() - new Date(b.metric_date).getTime())
                                        .slice(-4);

                                    const currentMonth = new Date(latestReport.reportMonth);
                                    const lastMonthVal = reports.find(r => {
                                        const d = new Date(r.reportMonth);
                                        return d.getMonth() === (currentMonth.getMonth() === 0 ? 11 : currentMonth.getMonth() - 1) &&
                                               d.getFullYear() === (currentMonth.getMonth() === 0 ? currentMonth.getFullYear() - 1 : currentMonth.getFullYear());
                                    })?.metrics.find(rm => rm.metricDefinition.metricKey === mKey)?.metricValue;

                                    return (
                                        <MetricRenderer 
                                            key={mKey}
                                            displayType={m.metricDefinition.displayType}
                                            metricData={{
                                                metricKey: mKey,
                                                displayName: m.metricDefinition.displayName,
                                                currentValue: Number(m.metricValue || 0),
                                                previousValue: lastMonthVal !== undefined && lastMonthVal !== null ? Number(lastMonthVal) : null,
                                                unit: m.metricDefinition.metricUnit,
                                                improvementDirection: m.metricDefinition.improvementDirection as 'increase' | 'decrease',
                                                history
                                            }}
                                            theme="light"
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ));
                })()}
              </div>

              <div className="space-y-8">
                {/* Streak card */}
                <div className="bg-black rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl">
                   <div className="absolute top-[-20%] right-[-10%] w-60 h-60 bg-[#AAD816]/10 rounded-full blur-[100px]" />
                   <div className="relative z-10 flex flex-col justify-between h-full space-y-6">
                     <div>
                        <p className="text-[10px] text-[#AAD816] font-black uppercase tracking-[0.2em] mb-1">Consistency Streak</p>
                        <div className="flex items-baseline gap-2">
                           <span className="text-6xl font-black italic tracking-tighter">{streak}</span>
                           <span className="text-sm font-bold text-gray-500 uppercase">Months</span>
                        </div>
                     </div>
                     <div className="flex items-center gap-4">
                        <div className="flex-1 h-2 bg-gray-900 rounded-full overflow-hidden">
                           <div 
                            className="h-full bg-[#AAD816] shadow-[0_0_15px_rgba(170,216,22,0.5)] transition-all duration-1000" 
                            style={{ width: `${Math.min(streak * 10, 100)}%` }}
                           />
                        </div>
                        <span className="text-2xl">🔥</span>
                     </div>
                     <p className="text-[10px] text-gray-400 font-medium italic">Maintaining consistency for {streak} months.</p>
                   </div>
                </div>

                <div className="bg-[#AAD816] rounded-[2.5rem] p-8 text-black shadow-xl shadow-[#AAD816]/10 group">
                   <h4 className="text-xs font-black uppercase tracking-widest mb-4 italic">AI Insight</h4>
                   <p className="text-xs font-bold leading-relaxed opacity-70">
                     {latestSummary?.aiInsight || "Recording more assessments will unlock deeper AI health insights."}
                   </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <LogStaffProgressModal 
        isOpen={showLogModal} 
        onClose={() => setShowLogModal(false)}
        memberId={memberId!}
        memberName={memberName}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default MemberProgressGymModal;
